import { usePrivy, useWallets } from "@privy-io/react-auth";
import {
	useWallets as useSolanaWallets,
} from "@privy-io/react-auth/solana";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useRouter } from "next/navigation";
import { privyAuthService } from "@/services/privy-auth.service";
import Cookies from "js-cookie";
import { COOKIE_NAME } from "@/constants/middleware.constants";
import { IUser } from "@/models/User";
import { useAuthSession } from "@/contexts/AuthSessionContext";
import { markRuntimeError, markRuntimeStep } from "@/lib/runtime-diagnostics";
import { MYGEOTOKENS_WALLET_DISPLAY_NAME } from "@/constants/wallet-branding";
import { useAccountLinking, UseAccountLinkingReturn, MergeConflictData, LinkConflictType } from "@/hooks/useAccountLinking";
import { useWalletSelection } from "@/contexts/WalletSelectionContext";
import { dispatchPrivyReset } from "@/lib/privy-reset";
export type { MergeConflictData };

interface SolanaWallet {
	walletClientType: "phantom" | "privy" | "solflare";
	address?: string;
	walletAddress?: string;
	chainType?: "solana";
	signTransaction?: (transaction: any) => Promise<any>;
	signMessage?: (message: Uint8Array) => Promise<any>;
}

interface PrivyLinkedAccount {
	type: "wallet" | "smart_wallet";
	chainType?: string;
	address?: string;
	walletAddress?: string;
	walletClientType?: string;
}

function isSolanaWallet(wallet: any): wallet is SolanaWallet {
	return (
		wallet &&
		(wallet.walletClientType === "phantom" ||
			wallet.walletClientType === "privy" ||
			wallet.walletClientType === "solflare" ||
			wallet.chainType === "solana")
	);
}

function isPrivyLinkedAccount(account: any): account is PrivyLinkedAccount {
	return (
		account &&
		(account.type === "wallet" || account.type === "smart_wallet") &&
		(account.chainType === "solana" || account.chainType === undefined)
	);
}

function hasSigningCapabilities(
	wallet: any
): wallet is SolanaWallet & Required<Pick<SolanaWallet, "signTransaction" | "signMessage">> {
	return (
		wallet &&
		typeof wallet.signTransaction === "function" &&
		typeof wallet.signMessage === "function"
	);
}

type EffectiveWallet = SolanaWallet | PrivyLinkedAccount | null;

function toTransactionBytes(transaction: any): Uint8Array {
	if (transaction instanceof Uint8Array) return transaction;
	if (ArrayBuffer.isView(transaction)) {
		return new Uint8Array(transaction.buffer, transaction.byteOffset, transaction.byteLength);
	}
	if (transaction?.serialize && typeof transaction.serialize === "function") {
		return transaction.serialize({ requireAllSignatures: false, verifySignatures: false });
	}
	throw new Error("Unsupported transaction format");
}

function toSignedTransactionShape(originalTransaction: any, signedTransaction: Uint8Array): any {
	if (!signedTransaction) return signedTransaction;
	const txConstructor = originalTransaction?.constructor as
		| { deserialize?: (bytes: Uint8Array) => any; from?: (bytes: Uint8Array) => any }
		| undefined;
	if (typeof txConstructor?.deserialize === "function") {
		try { return txConstructor.deserialize(signedTransaction); } catch { /* fall through */ }
	}
	if (typeof txConstructor?.from === "function") {
		try { return txConstructor.from(signedTransaction); } catch { /* fall through */ }
	}
	return signedTransaction;
}

// Safe base64url decode — iOS Safari is stricter about padding than Chrome/Firefox
function safeBase64Decode(str: string): string {
	const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
	const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, "=");
	return atob(padded);
}

type AuthStateType = "idle" | "checking_cookie" | "authenticating" | "authenticated" | "error" | "timeout";

interface AuthState {
	state: AuthStateType;
	isAuthenticating: boolean;
	lastAuthTime: number;
	authPromise: Promise<IUser | null> | null;
	error?: string;
}

interface UsePrivyWalletReturn extends UseAccountLinkingReturn {
	isSignedIn: boolean;
	authenticated: boolean;
	user: IUser | undefined;
	privyUser: any;
	ready: boolean;
	walletsReady: boolean;
	setUser: (user: IUser | undefined) => void;
	isConnected: boolean;
	publicKey: string | null;
	walletType: string | null;
	solanaWallet: EffectiveWallet;
	isConnecting: boolean;
	connectedWallets: EffectiveWallet[];
	activeWallet: EffectiveWallet;
	switchWallet: (address: string) => Promise<boolean>;
	canSwitchWallets: boolean;
	login: (preferredMethod?: "phantom" | "email" | "sms") => Promise<void>;
	logout: () => Promise<void>;
	signTransaction: (transaction: any, privySignTx?: (args: any) => Promise<any>) => Promise<any>;
	signMessage: (message: Uint8Array, privySignMsg?: (args: any) => Promise<any>) => Promise<any>;
	getBalance: () => Promise<number>;
	connect: (preferredMethod?: "phantom" | "email" | "sms") => Promise<void>;
	disconnect: () => Promise<void>;
	provider: EffectiveWallet;
	signer: {
		signTransaction: (transaction: any) => Promise<any>;
		signMessage: (message: Uint8Array) => Promise<any>;
		publicKey: string | null;
		signAllTransactions: (transactions: any[]) => Promise<any[]>;
	} | null;
}

export function usePrivyWallet(): UsePrivyWalletReturn {
	const { login, logout: privyLogout, authenticated, user: privyUserRaw, ready } = usePrivy();
	const router = useRouter();
	const { sessionUser, setSessionUser, hasSessionToken } = useAuthSession();
	// Shared across ALL hook instances — switching in WalletSwitcher propagates to
	// the wallet page, header, and any other consumer without a page reload.
	const { selectedWalletAddress, setSelectedWalletAddress } = useWalletSelection();

	const accountLinking = useAccountLinking();

	const walletsState = useWallets() as { wallets?: any[]; ready?: boolean };
	const walletsRaw = walletsState.wallets || [];
	const genericWalletsReady = walletsState.ready === true;

	const solanaWalletsState = useSolanaWallets() as { wallets?: any[]; ready?: boolean };
	const { wallets: solanaWalletsRaw = [], ready: solanaWalletsReadyRaw } = solanaWalletsState;
	const solanaWalletsReady = solanaWalletsReadyRaw === true;
	const walletsReady = genericWalletsReady && solanaWalletsReady;

	// Stable wallet refs — Privy returns new array refs on every poll
	const prevWalletsKeyRef = useRef("");
	const stableWalletsRef = useRef<any[]>([]);
	const walletsKey = walletsRaw
		.map((w: any) => {
			const address = w?.address || "";
			const type = (w as any)?.walletClientType || "";
			const hasSignTx = typeof (w as any)?.signTransaction === "function";
			const hasSignMsg = typeof (w as any)?.signMessage === "function";
			return `${address}:${type}:${hasSignTx ? "1" : "0"}:${hasSignMsg ? "1" : "0"}`;
		})
		.join(",");
	if (walletsKey !== prevWalletsKeyRef.current) {
		prevWalletsKeyRef.current = walletsKey;
		stableWalletsRef.current = walletsRaw;
	}
	const wallets = stableWalletsRef.current;

	const prevSolanaKeyRef = useRef("");
	const stableSolanaRef = useRef<any[]>([]);
	const solanaKey = solanaWalletsRaw
		.map((w: any) => {
			const address = w?.address || "";
			const type = (w as any)?.walletClientType || "";
			const hasSignTx = typeof (w as any)?.signTransaction === "function";
			const hasSignMsg = typeof (w as any)?.signMessage === "function";
			return `${address}:${type}:${hasSignTx ? "1" : "0"}:${hasSignMsg ? "1" : "0"}`;
		})
		.join(",");
	if (solanaKey !== prevSolanaKeyRef.current) {
		prevSolanaKeyRef.current = solanaKey;
		stableSolanaRef.current = solanaWalletsRaw;
	}
	const solanaWallets = stableSolanaRef.current;

	// Stable privyUser ref
	const prevPrivyUserKeyRef = useRef<string>("");
	const stablePrivyUserRef = useRef<any>(privyUserRaw);
	const privyUserKey = privyUserRaw
		? [
				privyUserRaw.id || "",
				...(privyUserRaw.linkedAccounts || [])
					.map((a: any) => a.address || a.walletAddress || "")
					.sort(),
		  ].join("|")
		: "";
	if (privyUserKey !== prevPrivyUserKeyRef.current) {
		prevPrivyUserKeyRef.current = privyUserKey;
		stablePrivyUserRef.current = privyUserRaw;
	}
	const privyUser = stablePrivyUserRef.current;

	// NOTE: useSignTransaction / useSignMessage intentionally NOT called here.
	// Those hooks subscribe on mount and cause GPU/memory crashes on iOS Safari
	// when combined with the other wallet subscriptions. They are consumed only
	// on pages that actually need signing, via the usePrivySigner hook.

	const [isConnecting, setIsConnecting] = useState(false);

	const isMobile = useMemo(() => {
		if (typeof window === "undefined") return false;
		return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
	}, []);

	const isMountedRef = useRef(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => { isMountedRef.current = false; };
	}, []);

	const currentUserRef = useRef<IUser | undefined>(undefined);
	currentUserRef.current = sessionUser;

	const authStateRef = useRef<AuthState>({
		state: "idle",
		isAuthenticating: false,
		lastAuthTime: 0,
		authPromise: null,
	});
	const authAttemptedRef = useRef(false);
	const authTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const authRetryCountRef = useRef(0);
	const MAX_AUTH_RETRIES = 3;

	const connectedWallets = useMemo((): EffectiveWallet[] => {
		const allWallets: EffectiveWallet[] = [];

		const seenAddresses = new Set<string>();
		const candidateWallets = [
			...wallets.filter(isSolanaWallet),
			...solanaWallets.filter(isSolanaWallet),
		];
		for (const wallet of candidateWallets) {
			const address = wallet?.address || (wallet as any)?.walletAddress;
			if (!address || seenAddresses.has(address)) continue;
			seenAddresses.add(address);
			allWallets.push(wallet);
		}

		if (privyUser?.linkedAccounts) {
			const walletAccounts = (privyUser.linkedAccounts as PrivyLinkedAccount[]).filter(
				(account: PrivyLinkedAccount) =>
					account.type === "wallet" || account.type === "smart_wallet"
			);
			const linkedWallets = walletAccounts.filter(isPrivyLinkedAccount);
			linkedWallets.forEach((linkedWallet: PrivyLinkedAccount) => {
				if (!linkedWallet) return;
				const walletAddress = linkedWallet.address || (linkedWallet as any).walletAddress;
				const exists = allWallets.some((existing) => {
					if (!existing) return false;
					return (existing.address || (existing as any).walletAddress) === walletAddress;
				});
				if (!exists) allWallets.push(linkedWallet);
			});
		}

		return allWallets.filter(Boolean);
	}, [wallets, solanaWallets, privyUser]);

	// Restore persisted wallet preference on mount / userId change (desktop only).
	// Also seeds the shared context with the correct login-method default when no
	// explicit preference has been saved yet.
	useEffect(() => {
		if (!sessionUser?.id || isMobile) return;

		// 1 — Respect an explicit user preference stored in localStorage
		const saved = localStorage.getItem(`walletPref_${sessionUser.id}`);
		if (
			saved &&
			connectedWallets.some(
				(w) => w && (w.address || (w as any).walletAddress) === saved
			)
		) {
			setSelectedWalletAddress(saved);
			return;
		}

		// 2 — No saved preference: auto-select based on how the user logs in.
		//     If any non-wallet account (email/phone/social) is linked, the user
		//     authenticates via social → default to the Privy embedded wallet.
		//     Wallet-only users → default to Phantom.
		const hasSocialOrEmailAccount = (privyUser?.linkedAccounts || []).some(
			(a: any) =>
				["email", "phone", "twitter_oauth", "google_oauth", "discord_oauth"].includes(a.type)
		);

		if (!hasSocialOrEmailAccount) {
			// Wallet-only user → prefer Phantom
			const phantom = connectedWallets.find(
				(w) => w && w.walletClientType === "phantom"
			);
			if (phantom) {
				const addr = phantom.address || (phantom as any).walletAddress;
				if (addr) { setSelectedWalletAddress(addr); return; }
			}
		}

		// Social/email user (or fallback) → prefer Privy embedded wallet
		const privyWallet = connectedWallets.find(
			(w) => w && w.walletClientType === "privy"
		);
		if (privyWallet) {
			const addr = privyWallet.address || (privyWallet as any).walletAddress;
			if (addr) setSelectedWalletAddress(addr);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionUser?.id, isMobile]);

	const activeWallet = useMemo((): EffectiveWallet => {
		if (connectedWallets.length === 0) return null;

		if (isMobile) {
			// Mobile: always use the Privy embedded wallet — Phantom cannot sign on mobile
			return (
				connectedWallets.find((w) => w?.walletClientType === "privy") ??
				connectedWallets[0]
			);
		}

		// Desktop: explicit/persisted selection has highest priority
		if (selectedWalletAddress) {
			const selected = connectedWallets.find(
				(wallet) => wallet && (wallet.address || (wallet as any).walletAddress) === selectedWalletAddress
			);
			if (selected) return selected;
		}

		// No explicit selection yet — derive default from login method so the
		// correct wallet is active from the very first render (before the
		// restore effect seeds selectedWalletAddress into the context).
		//
		// Rule: if the user has any non-wallet account (email/phone/social),
		// they authenticate via social → Privy embedded wallet is the default.
		// Wallet-only accounts (no email/social linked) → Phantom is the default.
		const hasSocialOrEmailAccount = (privyUser?.linkedAccounts || []).some(
			(a: any) =>
				["email", "phone", "twitter_oauth", "google_oauth", "discord_oauth"].includes(a.type)
		);

		if (!hasSocialOrEmailAccount) {
			// Wallet-only user → prefer Phantom
			const phantom = connectedWallets.find(
				(w) => w && w.walletClientType === "phantom"
			);
			if (phantom) return phantom;
		}

		// Social/email user (or fallback) → prefer Privy embedded wallet
		return (
			connectedWallets.find((w) => w && w.walletClientType === "privy") ??
			connectedWallets[0]
		);
	}, [connectedWallets, selectedWalletAddress, isMobile, privyUser?.linkedAccounts]);

	const effectiveWallet = activeWallet;

	const walletProperties = useMemo(() => {
		const isConnected = authenticated || !!effectiveWallet;
		const publicKey = effectiveWallet
			? effectiveWallet.address || effectiveWallet.walletAddress || null
			: null;
		const walletType = effectiveWallet
			? effectiveWallet.walletClientType || (isPrivyLinkedAccount(effectiveWallet) ? "privy" : null)
			: null;
		return { isConnected, publicKey, walletType };
	}, [authenticated, effectiveWallet]);

	const cleanupAuthState = useCallback(() => {
		authStateRef.current = {
			state: "idle",
			isAuthenticating: false,
			lastAuthTime: 0,
			authPromise: null,
		};
		authAttemptedRef.current = false;
		if (authTimeoutRef.current) {
			clearTimeout(authTimeoutRef.current);
			authTimeoutRef.current = null;
		}
	}, []);

	useEffect(() => {
		if (privyUser && sessionUser && sessionUser.privyId !== privyUser.id) {
			cleanupAuthState();
			setSessionUser(undefined);
		}
	}, [privyUser, sessionUser, cleanupAuthState, setSessionUser]);

	useEffect(() => {
		const authenticateWithBackend = async () => {
			if (!authenticated || !privyUser || !ready) return;
			if (authAttemptedRef.current && authRetryCountRef.current >= MAX_AUTH_RETRIES) return;

			// Social logins (Twitter, Google, Discord) complete before Privy has finished
			// provisioning the embedded Solana wallet. Silently bail and wait — the effect
			// will re-run once privyUser updates with the new wallet in linkedAccounts.
			const hasSolanaWallet = (privyUser.linkedAccounts || []).some(
				(a: any) =>
					(a.type === "wallet" || a.type === "smart_wallet") &&
					a.chainType === "solana"
			);
			if (!hasSolanaWallet) return;

			const existingUserCookie = Cookies.get(COOKIE_NAME);
			if (existingUserCookie) {
				try {
					const existingUser = JSON.parse(existingUserCookie) as IUser;
					if (existingUser.id && existingUser.privyId === privyUser.id) {
						const token = (existingUser as any).token;
						if (token) {
							try {
								// Fixed: safe base64url decode for iOS Safari
								const payload = JSON.parse(safeBase64Decode(token.split(".")[1]));
								const isTokenExpired = payload.exp && Date.now() >= payload.exp * 1000;
								const tokenAge = Date.now() - (payload.iat ? payload.iat * 1000 : 0);
								const MAX_TOKEN_AGE = 60 * 60 * 1000;
								if (!isTokenExpired && tokenAge < MAX_TOKEN_AGE) {
									const latestUser = currentUserRef.current;
									if (!latestUser || latestUser.id !== existingUser.id) {
										setSessionUser(existingUser);
									}
									authRetryCountRef.current = 0;
									return;
								}
							} catch (tokenError) {
								// Invalid token format, proceed with auth
							}
						}
					}
				} catch (error) {
					// Invalid cookie, proceed with auth
				}
			}

			const now = Date.now();
			const timeSinceLastAuth = now - authStateRef.current.lastAuthTime;
			const AUTH_TIMEOUT = 30000;
			const RETRY_COOLDOWN = 5000;

			if (authAttemptedRef.current && timeSinceLastAuth < RETRY_COOLDOWN) return;

			if (authStateRef.current.state === "authenticating" && timeSinceLastAuth < 5000) {
				if (authStateRef.current.authPromise) {
					try {
						const result = await authStateRef.current.authPromise;
						if (result && isMountedRef.current) {
							const latestUser = currentUserRef.current;
							if (!latestUser || latestUser.id !== result.id) {
								setSessionUser(result);
							}
							setIsConnecting(false);
							authStateRef.current.state = "authenticated";
							authRetryCountRef.current = 0;
						}
					} catch (error) {
						authStateRef.current.state = "error";
						authStateRef.current.error = error instanceof Error ? error.message : "Unknown error";
						cleanupAuthState();
					}
				}
				return;
			}

			authAttemptedRef.current = true;
			authRetryCountRef.current += 1;
			authStateRef.current.state = "authenticating";
			authStateRef.current.isAuthenticating = true;
			authStateRef.current.lastAuthTime = now;
			setIsConnecting(true);

			authTimeoutRef.current = setTimeout(() => {
				authStateRef.current.state = "timeout";
				authStateRef.current.error = "Authentication timeout";
				cleanupAuthState();
				setIsConnecting(false);
			}, AUTH_TIMEOUT);

			try {
				const authPromise = privyAuthService.authenticateWithBackend(privyUser);
				authStateRef.current.authPromise = authPromise;
				const backendUser = await authPromise;

			if (backendUser && isMountedRef.current) {
				const isProduction =
					process.env.NEXT_PUBLIC_ENV === "production" || process.env.NODE_ENV === "production";
				const isSecureContext =
					typeof window !== "undefined" &&
					(window.location.protocol === "https:" ||
						process.env.NEXT_PUBLIC_FORCE_SECURE_COOKIES === "true" ||
						isProduction);

				Cookies.set(COOKIE_NAME, JSON.stringify(backendUser), {
					expires: 1,
					secure: isSecureContext,
					sameSite: "lax",
					path: "/",
				});
				const latestUser = currentUserRef.current;
				if (!latestUser || latestUser.id !== backendUser.id) {
					setSessionUser(backendUser);
				}
				authStateRef.current.state = "authenticated";
				authRetryCountRef.current = 0;

				// First-time login: flag for onboarding and redirect to profile
				if (backendUser.isNewUser && typeof window !== "undefined") {
					sessionStorage.setItem("mgt_welcome", "1");
					if (!window.location.pathname.startsWith("/profile")) {
						router.push("/profile");
					}
				}
			} else if (!backendUser) {
					authStateRef.current.state = "error";
					authStateRef.current.error = "Backend authentication failed";
				}
			} catch (error) {
				authStateRef.current.state = "error";
				authStateRef.current.error = error instanceof Error ? error.message : "Unknown error";
				if (process.env.NEXT_PUBLIC_ENV === "local") {
					console.error(`Authentication error (attempt ${authRetryCountRef.current}):`, error);
				}
			} finally {
				if (isMountedRef.current) setIsConnecting(false);
				if (authTimeoutRef.current) {
					clearTimeout(authTimeoutRef.current);
					authTimeoutRef.current = null;
				}
			}
		};

		authenticateWithBackend();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authenticated, privyUser, ready, cleanupAuthState, setSessionUser]);

	const logout = useCallback(async () => {
		try {
			Cookies.remove(COOKIE_NAME);
			setSessionUser(undefined);
			setIsConnecting(false);
			cleanupAuthState();
			// Clear any in-flight linking error / merge state so the next session
			// starts clean (Tasks 5, 9).
			accountLinking.clearSyncError();
			accountLinking.clearMergeConflict();
			await privyLogout();
		} catch (error) {
			console.error("[usePrivyWallet] Logout error:", error);
			cleanupAuthState();
			setIsConnecting(false);
		} finally {
			// Force PrivyProvider to remount so the SDK's internal login state
			// (flow step, pending OTP, error) cannot leak into the next attempt.
			// Fixes "OTP entry skipped on second login" for both phone and email
			// (Tasks 6, 7).
			dispatchPrivyReset();
			try {
				localStorage.removeItem("auth_token");
				localStorage.removeItem("phantom_address_to_link");
				localStorage.removeItem("phantom_wallet_ready");
			} catch {
				// Ignore environments where localStorage is unavailable.
			}
			router.push("/");
		}
	}, [privyLogout, cleanupAuthState, accountLinking, router, setSessionUser]);

	const connect = useCallback(
		async (preferredMethod?: "phantom" | "email" | "sms") => {
			if (authenticated) return;
			setIsConnecting(true);
			try {
				if (preferredMethod === "phantom") {
					const phantom = (window as any).solana;
					if (!phantom?.isPhantom)
						throw new Error(
							`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not found. Please install it.`
						);
					await login({ loginMethods: ["wallet"], walletChainType: "solana-only" });
				} else if (preferredMethod === "email") {
					await login({ loginMethods: ["email"], walletChainType: "solana-only" });
				} else if (preferredMethod === "sms") {
					await login({ loginMethods: ["sms"], walletChainType: "solana-only" });
				} else {
					await login({ walletChainType: "solana-only" });
				}
			} catch (error: any) {
				throw error;
			} finally {
				setIsConnecting(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[authenticated, login]
	);

	// signTransaction accepts optional privySignTx injected from usePrivySigner
	// so pages that need signing can pass it in without mounting the hook globally
	const signTransaction = useCallback(
		async (transaction: any, privySignTx?: (args: any) => Promise<any>) => {
			markRuntimeStep("sign_tx_enter");
			// Privy readiness flags can briefly lag behind an already-connected wallet
			// on some sessions. Only block when we truly have no active wallet.
			if ((!ready || !walletsReady) && !effectiveWallet) {
				markRuntimeStep("sign_tx_blocked_not_ready", { ready, walletsReady });
				throw new Error("Wallet is still initializing");
			}
			if (!effectiveWallet) throw new Error("No Solana wallet connected");

			const walletAddress = effectiveWallet.address || (effectiveWallet as any).walletAddress;
			const solanaWallet =
				solanaWallets.find((w) => w.address === walletAddress) ||
				wallets.find(
					(w) =>
						(w?.address || (w as any)?.walletAddress) === walletAddress
				);
			const walletType = solanaWallet?.walletClientType || effectiveWallet.walletClientType;
			const walletTypeLower = walletType?.toLowerCase();
			const isEmbeddedWallet = walletTypeLower === "privy";
			const isExternalWallet = walletTypeLower === "phantom" || walletTypeLower === "solflare";

			if (isEmbeddedWallet) {
				if (!solanaWallet)
					throw new Error(
						`${MYGEOTOKENS_WALLET_DISPLAY_NAME} is not available for signing`
					);
				if (!privySignTx)
					throw new Error(
						`${MYGEOTOKENS_WALLET_DISPLAY_NAME} signing is not ready on this page. Use usePrivySigner.`
					);
				try {
					markRuntimeStep("sign_tx_embedded_start");
					const txBytes = toTransactionBytes(transaction);
					const signedTransactionResult = await privySignTx({ transaction: txBytes, wallet: solanaWallet as any });
					const signedTransactionBytes = (signedTransactionResult as any)?.signedTransaction ?? signedTransactionResult;
					markRuntimeStep("sign_tx_embedded_success");
					return toSignedTransactionShape(transaction, signedTransactionBytes);
				} catch (error: any) {
					markRuntimeError("sign_tx_embedded", error);
					throw new Error(
						error.message?.includes("rejected") || error.message?.includes("cancelled") || error.message?.includes("denied")
							? error.message
							: `${MYGEOTOKENS_WALLET_DISPLAY_NAME} signing failed: ${error.message ?? "Unknown error"}. Please try again.`
					);
				}
			}

			if (isExternalWallet && typeof window !== "undefined") {
				const phantom = (window as any).solana;
				const solflare = (window as any).solflare;
				const walletProvider = walletTypeLower === "phantom" ? phantom : solflare;

				if (walletProvider?.signTransaction) {
					try {
						if (!walletProvider.isConnected && !walletProvider.publicKey) {
							try { await walletProvider.connect(); } catch { /* continue anyway */ }
						}
						const signedTransaction = await walletProvider.signTransaction(transaction);
						if (signedTransaction) return signedTransaction;
					} catch (directError: any) {
						if (process.env.NEXT_PUBLIC_ENV === "local") {
							console.error(`Direct ${walletType} signing failed:`, directError.message || directError);
						}
						if (
							directError.message?.includes("rejected") ||
							directError.message?.includes("cancelled") ||
							directError.message?.includes("denied") ||
							directError.message?.includes("User rejected") ||
							directError.code === 4001
						) throw new Error("Transaction cancelled by user");
					}
				}

				if (solanaWallet && privySignTx) {
					try {
						const signedTransactionResult = await privySignTx({
							transaction: toTransactionBytes(transaction),
							wallet: solanaWallet as any,
						});
						const signedTransactionBytes =
							(signedTransactionResult as any)?.signedTransaction || signedTransactionResult;
						const signedTransaction = toSignedTransactionShape(transaction, signedTransactionBytes);
						if (signedTransaction) return signedTransaction;
					} catch (error: any) {
						if (process.env.NEXT_PUBLIC_ENV === "local") {
							console.error("Privy signTransaction hook failed:", error.message || error);
						}
						if (
							error.message?.includes("rejected") ||
							error.message?.includes("cancelled") ||
							error.message?.includes("denied")
						) throw new Error("Transaction cancelled by user");
					}
				}
			}

			if (solanaWallet && typeof solanaWallet.signTransaction === "function") {
				try {
					const result = await solanaWallet.signTransaction(transaction);
					if (result) return result;
				} catch (p3Error: any) {
					if (
						p3Error.message?.includes("rejected") ||
						p3Error.message?.includes("cancelled") ||
						p3Error.message?.includes("denied")
					) throw new Error("Transaction cancelled by user");
				}
			}

			const privyWallet = wallets.find((w) => w.address === walletAddress);
			if (privyWallet && typeof (privyWallet as any).signTransaction === "function") {
				try {
					const result = await (privyWallet as any).signTransaction(transaction);
					if (result) return result;
				} catch (p4Error: any) {
					if (
						p4Error.message?.includes("rejected") ||
						p4Error.message?.includes("cancelled") ||
						p4Error.message?.includes("denied")
					) throw new Error("Transaction cancelled by user");
				}
			}

			if (hasSigningCapabilities(effectiveWallet)) {
				try {
					const result = await effectiveWallet.signTransaction(transaction);
					if (result) return result;
				} catch (p5Error: any) {
					if (
						p5Error.message?.includes("rejected") ||
						p5Error.message?.includes("cancelled") ||
						p5Error.message?.includes("denied")
					) throw new Error("Transaction cancelled by user");
				}
			}

			throw new Error(
				`Wallet does not support transaction signing (type: ${walletType}). Please try disconnecting and reconnecting your wallet.`
			);
		},
		[effectiveWallet, ready, walletsReady, wallets, solanaWallets]
	);

	// signMessage accepts optional privySignMsg injected from usePrivySigner
	const signMessage = useCallback(
		async (message: Uint8Array, privySignMsg?: (args: any) => Promise<any>) => {
			// Same transient-ready guard as signTransaction.
			if ((!ready || !walletsReady) && !effectiveWallet) {
				throw new Error("Wallet is still initializing");
			}
			if (!effectiveWallet) throw new Error("No Solana wallet connected");

			const walletAddress = effectiveWallet.address || (effectiveWallet as any).walletAddress;
			const solanaWallet =
				solanaWallets.find((w) => w.address === walletAddress) ||
				wallets.find(
					(w) =>
						(w?.address || (w as any)?.walletAddress) === walletAddress
				);
			const walletType = solanaWallet?.walletClientType || effectiveWallet.walletClientType;
			const walletTypeLower = walletType?.toLowerCase();
			const isEmbeddedWallet = walletTypeLower === "privy";
			const isExternalWallet = walletTypeLower === "phantom" || walletTypeLower === "solflare";

			if (isEmbeddedWallet) {
				if (!solanaWallet)
					throw new Error(
						`${MYGEOTOKENS_WALLET_DISPLAY_NAME} is not available for signing`
					);
				if (!privySignMsg)
					throw new Error(
						`${MYGEOTOKENS_WALLET_DISPLAY_NAME} signing is not ready on this page. Use usePrivySigner.`
					);
				try {
					const signatureResult = await privySignMsg({ message, wallet: solanaWallet as any });
					return (signatureResult as any)?.signature || signatureResult;
				} catch (error: any) {
					if (
						error.message?.includes("rejected") ||
						error.message?.includes("cancelled") ||
						error.message?.includes("denied")
					) throw error;
				}
			}

			if (isExternalWallet && typeof window !== "undefined") {
				const phantom = (window as any).solana;
				const solflare = (window as any).solflare;
				const walletProvider = walletTypeLower === "phantom" ? phantom : solflare;

				if (walletProvider?.signMessage) {
					try {
						const signature = await walletProvider.signMessage(message, "utf8");
						if (signature) return signature;
					} catch (directError: any) {
						if (
							directError.message?.includes("rejected") ||
							directError.message?.includes("cancelled") ||
							directError.message?.includes("denied") ||
							directError.message?.includes("User rejected") ||
							directError.code === 4001
						) throw new Error("Message signing cancelled by user");
					}
				}

				if (solanaWallet && privySignMsg) {
					try {
						const signatureResult = await privySignMsg({ message, wallet: solanaWallet as any });
						const signature = (signatureResult as any)?.signature || signatureResult;
						if (signature) return signature;
					} catch (error: any) {
						if (
							error.message?.includes("rejected") ||
							error.message?.includes("cancelled") ||
							error.message?.includes("denied")
						) throw new Error("Message signing cancelled by user");
					}
				}
			}

			if (solanaWallet && typeof solanaWallet.signMessage === "function") {
				return await solanaWallet.signMessage(message);
			}

			const privyWallet = wallets.find((w) => w.address === walletAddress);
			if (privyWallet && typeof (privyWallet as any).signMessage === "function") {
				return await (privyWallet as any).signMessage(message);
			}

			if (hasSigningCapabilities(effectiveWallet)) {
				return await effectiveWallet.signMessage(message);
			}

			throw new Error(
				`Wallet does not support message signing (type: ${walletType}). Please try disconnecting and reconnecting your wallet.`
			);
		},
		[effectiveWallet, ready, walletsReady, wallets, solanaWallets]
	);

	const getBalance = useCallback(async (): Promise<number> => {
		if (!walletProperties.publicKey) return 0;
		try {
			return await privyAuthService.getWalletBalance(walletProperties.publicKey);
		} catch {
			return 0;
		}
	}, [walletProperties.publicKey]);

	const switchWallet = useCallback(
		async (address: string): Promise<boolean> => {
			try {
				const targetWallet = connectedWallets.find(
					(wallet) => wallet && (wallet.address || (wallet as any).walletAddress) === address
				);
				if (!targetWallet) return false;
				setSelectedWalletAddress(address);
				if (sessionUser?.id) {
					localStorage.setItem(`walletPref_${sessionUser.id}`, address);
				}
				return true;
			} catch {
				return false;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[connectedWallets, sessionUser?.id]
	);

	const canSwitchWallets = useMemo(() => connectedWallets.length > 1, [connectedWallets.length]);

	const signer = useMemo(() => {
		if (!effectiveWallet) return null;
		return {
			// NOTE: these partial signers work for external wallets.
			// For embedded (privy) wallets, callers must inject privySignTx/privySignMsg
			// via the optional second argument, using usePrivySigner on that page.
			signTransaction: (tx: any) => signTransaction(tx),
			signMessage: (msg: Uint8Array) => signMessage(msg),
			publicKey: walletProperties.publicKey,
			signAllTransactions: (transactions: any[]) =>
				Promise.all(transactions.map((tx) => signTransaction(tx))),
		};
	}, [effectiveWallet, signTransaction, signMessage, walletProperties.publicKey]);

	useEffect(() => {
		return () => { cleanupAuthState(); };
	}, [cleanupAuthState]);

	const isSignedIn = authenticated || hasSessionToken;

	return {
		...accountLinking,
		isSignedIn,
		authenticated,
		user: sessionUser,
		privyUser,
		ready,
		walletsReady,
		setUser: setSessionUser,
		isConnected: walletProperties.isConnected,
		publicKey: walletProperties.publicKey,
		walletType: walletProperties.walletType,
		solanaWallet: effectiveWallet,
		isConnecting,
		connectedWallets,
		activeWallet,
		switchWallet,
		canSwitchWallets,
		login: connect,
		logout,
		signTransaction,
		signMessage,
		getBalance,
		connect,
		disconnect: logout,
		provider: effectiveWallet,
		signer,
	};
}